import { supabase } from '@/lib/supabase';
import { getReferrerInfo, getUTMParameters } from './referrerTracking';
import { detectBrowser } from './device/browserDetection';
import { detectOS } from './device/osDetection';
import { detectPlatform } from './device/platformDetection';
import type { ClickEvent } from './types';

/**
 * LEGAL BASIS: GDPR Art. 6(1)(f) - Legitimate Interest
 * TDDDG § 25 COMPLIANT: No terminal device storage access
 * 
 * This module collects enhanced analytics data for non-consented users
 * while remaining fully compliant with German law:
 * 
 * ✅ ALLOWED (read-only, no storage access):
 * - navigator.userAgent (browser, OS, platform detection)
 * - navigator.language (language preference)
 * - document.referrer (traffic source)
 * - URL parameters (UTM tracking)
 * 
 * ❌ NOT COLLECTED (requires consent under TDDDG § 25):
 * - localStorage/sessionStorage access (session_id)
 * - Persistent identifiers
 * - Cross-session tracking
 * 
 * ⚠️ CONSERVATIVE (omitted to stay safe):
 * - screen.width/height (could contribute to fingerprint)
 * - window.innerWidth/Height (viewport size)
 * - Intl.DateTimeFormat timezone (disputed)
 */

// Helper function to check if the request is from Lovable editor
const isLovablePreview = (pagePath: string): boolean => {
  return pagePath.includes('forceHideBadge=true') || 
         pagePath.includes('__lovable_token=') ||
         window.location.search.includes('__lovable_token=');
};

// Detect automated test runs (Playwright/Selenium/etc.)
const isAutomatedTest = (pagePath: string): boolean => {
  if (typeof navigator !== 'undefined' && (navigator as any).webdriver) return true;
  if (typeof window !== 'undefined' && window.location?.search?.includes('e2e=1')) return true;
  if (pagePath.includes('e2e=1')) return true;
  return false;
};

// Sanitize page path for tracking (more aggressive - no query params except UTM)
const sanitizePagePath = (path: string): string => {
  // Remove all query parameters for privacy
  let cleanPath = path.split('?')[0];
  
  // Only allow alphanumeric, forward slashes, and hyphens
  cleanPath = cleanPath.replace(/[^a-zA-Z0-9/-]/g, '');
  
  // Normalize to max 100 chars
  return cleanPath.substring(0, 100);
};

/**
 * Enhanced device info collection using ONLY read-only browser APIs.
 * NO localStorage, sessionStorage, or cookies accessed.
 * 
 * Legal basis: Legitimate interest (Art. 6(1)(f) GDPR)
 * TDDDG § 25 compliant: No terminal device storage
 */
const getEnhancedDeviceInfo = () => {
  const userAgent = navigator.userAgent || 'unknown';
  const referrerInfo = getReferrerInfo(userAgent);
  const utmParams = getUTMParameters();
  
  return {
    // Read-only browser APIs - legally allowed without consent
    platform: detectPlatform(),
    browser: detectBrowser(),
    os: detectOS(),
    language: navigator.language || 'unknown',
    user_agent: userAgent,
    
    // Referrer tracking - read-only from document.referrer
    referrer_category: referrerInfo.category,
    referrer_source: referrerInfo.source,
    bot_type: referrerInfo.botType,
    
    // UTM parameters - from URL, no storage access
    utm_params: Object.keys(utmParams).length > 0 ? utmParams : undefined,
    
    // Timestamp for the event
    timestamp: new Date().toISOString(),
    
    // Mark as non-consented tracking for transparency
    tracking_tier: 'anonymous' as const,
  };
};

/**
 * Track a page view for non-consented users with enhanced data.
 * 
 * This writes to the unified usage_statistics table with:
 * - session_id = NULL (no localStorage access)
 * - user_id = NULL (no authentication tracking)
 * - Enhanced device_info with legally-allowed read-only data
 * 
 * Legal basis: Legitimate interest (Art. 6(1)(f) GDPR)
 * TDDDG § 25 compliant: No terminal device storage access
 */
export const trackAnonymousPageView = async (pagePath: string): Promise<void> => {
  try {
    // Filter out Lovable editor preview sessions
    if (isLovablePreview(pagePath)) {
      if (import.meta.env.DEV) {
        console.log('Skipping anonymous tracking for Lovable preview session');
      }
      return;
    }

    if (isAutomatedTest(pagePath)) {
      if (import.meta.env.DEV) console.log('Skipping anonymous tracking for automated test');
      return;
    }

    // Sanitize page path
    const sanitizedPath = sanitizePagePath(pagePath);
    
    // Get enhanced device info using only read-only APIs
    const deviceInfo = getEnhancedDeviceInfo();
    
    // Write to usage_statistics with NULL session_id (marks as anonymous)
    const { error } = await supabase.from('usage_statistics').insert({
      page_path: sanitizedPath,
      action_type: 'page_view',
      session_id: null, // NULL = anonymous tracking (no localStorage access)
      user_id: null, // NULL = not authenticated
      device_info: deviceInfo,
    });
    
    if (error) {
      throw error;
    }
    
    if (import.meta.env.DEV) {
      console.log('[Tier 1 Enhanced] Anonymous page view tracked:', { 
        pagePath: sanitizedPath,
        browser: deviceInfo.browser,
        os: deviceInfo.os,
        platform: deviceInfo.platform,
        language: deviceInfo.language,
        referrer: deviceInfo.referrer_source || deviceInfo.referrer_category,
        utm: deviceInfo.utm_params,
      });
    }
  } catch (error) {
    // Silently fail for analytics to not impact user experience
    if (import.meta.env.DEV) {
      console.error('Anonymous analytics tracking error:', error);
    }
  }
};

/**
 * Track a click event for non-consented users (Tier 1 - anonymous).
 * 
 * Legal basis: Legitimate interest (Art. 6(1)(f) GDPR)
 * TDDDG § 25 compliant: No terminal device storage
 * 
 * This function intentionally omits click_text and click_href as they
 * could contain PII. Only developer-defined track_label and track_category
 * are included as they are safe by design.
 */
export const trackAnonymousClick = async (clickData: ClickEvent): Promise<void> => {
  try {
    // Skip Lovable preview
    if (isLovablePreview(clickData.page_path)) {
      if (import.meta.env.DEV) {
        console.log('Skipping anonymous click tracking for Lovable preview session');
      }
      return;
    }

    if (isAutomatedTest(clickData.page_path)) {
      if (import.meta.env.DEV) console.log('Skipping anonymous click tracking for automated test');
      return;
    }

    const sanitizedPath = sanitizePagePath(clickData.page_path);
    const deviceInfo = getEnhancedDeviceInfo();
    
    // Anonymous click tracking - no PII, no session
    const enrichedDeviceInfo = {
      ...deviceInfo,
      click_element: clickData.element_type,
      click_label: clickData.track_label,     // Safe - developer-defined
      click_category: clickData.track_category, // Safe - developer-defined
      // Intentionally omit: click_text, click_href (could contain PII)
    };
    
    const { error } = await supabase.from('usage_statistics').insert({
      page_path: sanitizedPath,
      action_type: 'click',
      session_id: null,
      user_id: null,
      device_info: enrichedDeviceInfo,
    });
    
    if (error) throw error;
    
    if (import.meta.env.DEV) {
      console.log('[Tier 1] Anonymous click tracked:', {
        element: clickData.element_type,
        label: clickData.track_label,
        path: sanitizedPath,
        browser: deviceInfo.browser,
        referrer: deviceInfo.referrer_source || deviceInfo.referrer_category,
      });
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Anonymous click tracking error:', error);
    }
  }
};
